import { useEffect } from 'preact/hooks';
import { reportSentryError } from './error-handling';
import { isAndroidNativeApp } from './platform';

export function openExternalUrl(url: string) {
  if (isAndroidNativeApp) {
    try {
      window.Android!.sendMessage(JSON.stringify({ command: 'openExternalUrl', url }));
      return;
    } catch (error) {
      reportSentryError(error as Error, {
        context: 'Failed to open external URL with Android bridge',
        url,
      });
    }
  }

  window.open(url, '_blank', 'noopener,noreferrer');
}

export function useOpenExternalLinksInAndroidBrowser() {
  useEffect(() => {
    if (!isAndroidNativeApp) return;

    // Keep app routes inside the WebView, but send external destinations to Android
    // so they open in the user's default browser/mail app instead.
    const handleClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      if (!(event.target instanceof Element)) return;

      const anchor = event.target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;

      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }

      const isExternalHttpUrl =
        (url.protocol === 'http:' || url.protocol === 'https:') &&
        url.origin !== window.location.origin;
      const isExternalProtocol = url.protocol === 'mailto:';
      if (!isExternalHttpUrl && !isExternalProtocol) return;

      event.preventDefault();
      openExternalUrl(url.href);
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);
}
